import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, UserCog, IdCard, ShieldCheck, Stethoscope, GraduationCap } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const LICENSE_CLASSES = ["LMV", "LMV-NT", "LMV-TR", "MCWG", "MCWOG", "HMV", "HTV", "HPMV", "Trans", "Other"];
const STATUSES = ["Active", "On Leave", "Suspended", "Resigned", "Terminated"];
const DRIVER_TYPES = ["Permanent", "Contractor"];

const emptyRecord = { date: "", expiry_date: "", reference: "", file_url: "", file_public_id: "", notes: "" };
const emptyDriver = {
  id: "",
  name: "",
  driver_type: "Permanent",
  phone: "",
  email: "",
  date_of_birth: "",
  address: "",
  photo_url: "",
  photo_public_id: "",
  license_number: "",
  license_class: "LMV",
  license_issue_date: "",
  license_expiry_date: "",
  license_file_url: "",
  license_file_public_id: "",
  assigned_vehicle_id: "",
  training: { ...emptyRecord },
  medical_fitness: { ...emptyRecord },
  police_verification: { ...emptyRecord },
  joining_date: "",
  status: "Active",
  remarks: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-3" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

function ComplianceBlock({ title, icon: Icon, keyName, record, onChange, showExpiry = true, testidPrefix }) {
  const rec = record || emptyRecord;
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm p-4" data-testid={testidPrefix}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-sm bg-[#234333] text-white flex items-center justify-center"><Icon size={14} /></div>
        <div className="font-serif text-lg text-[#1c1a17]">{title}</div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Date">
          <input type="date" data-testid={`${testidPrefix}-date`} className={inputCls} value={rec.date || ""} onChange={(e) => onChange(keyName, { ...rec, date: e.target.value })} />
        </Field>
        {showExpiry && (
          <Field label="Expiry Date">
            <input type="date" data-testid={`${testidPrefix}-expiry`} className={inputCls} value={rec.expiry_date || ""} onChange={(e) => onChange(keyName, { ...rec, expiry_date: e.target.value })} />
          </Field>
        )}
        <Field label="Reference / Certificate No">
          <input data-testid={`${testidPrefix}-ref`} className={inputCls} value={rec.reference || ""} onChange={(e) => onChange(keyName, { ...rec, reference: e.target.value })} />
        </Field>
        <Field label="Document" full>
          <FileUploader
            kind="driver_document"
            label=""
            url={rec.file_url}
            onChange={({ url, public_id }) => onChange(keyName, { ...rec, file_url: url || "", file_public_id: public_id || "" })}
            testid={`${testidPrefix}-file`}
          />
        </Field>
        <Field label="Notes" full>
          <input data-testid={`${testidPrefix}-notes`} className={inputCls} maxLength={500} value={rec.notes || ""} onChange={(e) => onChange(keyName, { ...rec, notes: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

const statusColor = {
  "Active": "bg-green-100 text-green-800 border-green-300",
  "On Leave": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Suspended": "bg-orange-100 text-orange-800 border-orange-300",
  "Resigned": "bg-gray-100 text-gray-700 border-gray-300",
  "Terminated": "bg-red-100 text-red-800 border-red-300",
};

export default function Drivers() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor", "vehicle_admin"].includes(role);
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDriver);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [q, setQ] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [drivers, vehs] = await Promise.all([
        api.get("/admin/drivers"),
        api.get("/admin/vehicles").catch(() => ({ data: [] })),
      ]);
      setItems(drivers.data);
      setVehicles(vehs.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load drivers");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((d) => [d.name, d.phone, d.license_number, d.assigned_vehicle_registration].some((x) => (x || "").toLowerCase().includes(term)));
  }, [items, q]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm({ ...emptyDriver, training: { ...emptyRecord }, medical_fitness: { ...emptyRecord }, police_verification: { ...emptyRecord } }); setOpen(true); };
  const openEdit = (d) => {
    setForm({
      ...emptyDriver,
      ...d,
      training: { ...emptyRecord, ...(d.training || {}) },
      medical_fitness: { ...emptyRecord, ...(d.medical_fitness || {}) },
      police_verification: { ...emptyRecord, ...(d.police_verification || {}) },
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Driver name is required");
    const payload = {
      ...form,
      name: form.name.trim(),
      license_number: (form.license_number || "").trim(),
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/drivers/${form.id}`, payload);
        toast.success("Driver updated");
      } else {
        await api.post("/admin/drivers", payload);
        toast.success("Driver added");
      }
      setOpen(false); fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/drivers/${confirmDelete.id}`);
      toast.success("Driver deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-drivers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Vehicle Maintenance</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Drivers</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Personal details, driving licence, assignment and compliance records.</div>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} data-testid="drv-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Add Driver
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-white border border-[#e5e0d8] rounded-sm p-3">
        <input data-testid="drv-search" className={`${inputCls} flex-1 min-w-[220px]`} placeholder="Search name · phone · licence · assigned vehicle" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <UserCog size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No drivers match your filters.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f5f1e8] text-[10px] tracking-widest uppercase text-[#6b655b]">
              <tr>
                <th className="text-left px-4 py-3">Driver</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Licence</th>
                <th className="text-left px-4 py-3">Expiry</th>
                <th className="text-left px-4 py-3">Assigned Vehicle</th>
                <th className="text-left px-4 py-3">Status</th>
                {canEdit && <th className="text-right px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0ece2]">
              {filtered.map((d) => (
                <tr key={d.id} data-testid={`drv-row-${d.id}`} className="hover:bg-[#faf8f2]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1c1a17]">{d.name}</div>
                    <div className="text-[11px] text-[#8a8478]">{d.email || ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`drv-type-${d.id}`}
                      className={`inline-block text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-sm border ${
                        d.driver_type === "Contractor"
                          ? "bg-[#FEF7E4] text-[#8a5a1a] border-[#c78e42]/50"
                          : "bg-[#eef3ea] text-[#234333] border-[#234333]/30"
                      }`}
                    >
                      {d.driver_type || "Permanent"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{d.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-[#1c1a17]">{d.license_number || "—"}</div>
                    <div className="text-[11px] text-[#8a8478]">{d.license_class || ""}</div>
                  </td>
                  <td className="px-4 py-3">{d.license_expiry_date || "—"}</td>
                  <td className="px-4 py-3">{d.assigned_vehicle_registration || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-sm border ${statusColor[d.status] || "bg-gray-100 text-gray-700 border-gray-300"}`}>{d.status}</span>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(d)} data-testid={`drv-edit-${d.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><Pencil size={11} />Edit</button>
                      <button type="button" onClick={() => setConfirmDelete(d)} data-testid={`drv-delete-${d.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm ml-1"><Trash2 size={11} />Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Driver" : "Add Driver"}</DialogTitle>
            <DialogDescription>Personal details, driving licence, vehicle assignment, and compliance records.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Full Name *">
                <input data-testid="drv-input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
              </Field>
              <Field label="Type of Driver *">
                <select data-testid="drv-input-type" className={inputCls} value={form.driver_type} onChange={(e) => set("driver_type", e.target.value)}>
                  {DRIVER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Phone">
                <input data-testid="drv-input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </Field>
              <Field label="Email">
                <input data-testid="drv-input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
              </Field>
              <Field label="Date of Birth">
                <input data-testid="drv-input-dob" type="date" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
              </Field>
              <Field label="Joining Date">
                <input data-testid="drv-input-joining" type="date" className={inputCls} value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} />
              </Field>
              <Field label="Status">
                <select data-testid="drv-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Address" full>
                <textarea data-testid="drv-input-address" rows={2} className={`${inputCls} resize-y`} value={form.address} onChange={(e) => set("address", e.target.value)} />
              </Field>
              <Field label="Driver Photograph" full>
                <FileUploader
                  kind="driver_photo"
                  label=""
                  url={form.photo_url}
                  onChange={({ url, public_id }) => setForm((f) => ({ ...f, photo_url: url || "", photo_public_id: public_id || "" }))}
                  testid="drv-input-photo"
                />
              </Field>
            </div>

            <div className="bg-white border border-[#e5e0d8] rounded-sm p-4" data-testid="drv-license-block">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-sm bg-[#234333] text-white flex items-center justify-center"><IdCard size={14} /></div>
                <div className="font-serif text-lg text-[#1c1a17]">Driving Licence</div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Licence Number">
                  <input data-testid="drv-license-no" className={inputCls} value={form.license_number} onChange={(e) => set("license_number", e.target.value.toUpperCase())} />
                </Field>
                <Field label="Class">
                  <select data-testid="drv-license-class" className={inputCls} value={form.license_class} onChange={(e) => set("license_class", e.target.value)}>
                    {LICENSE_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Assigned Vehicle">
                  <select data-testid="drv-input-vehicle" className={inputCls} value={form.assigned_vehicle_id} onChange={(e) => set("assigned_vehicle_id", e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration_number} — {v.make} {v.model_name}</option>)}
                  </select>
                </Field>
                <Field label="Issue Date">
                  <input type="date" data-testid="drv-license-issue" className={inputCls} value={form.license_issue_date} onChange={(e) => set("license_issue_date", e.target.value)} />
                </Field>
                <Field label="Expiry Date">
                  <input type="date" data-testid="drv-license-expiry" className={inputCls} value={form.license_expiry_date} onChange={(e) => set("license_expiry_date", e.target.value)} />
                </Field>
                <Field label="Licence Document">
                  <FileUploader
                    kind="driver_document"
                    label=""
                    url={form.license_file_url}
                    onChange={({ url, public_id }) => setForm((f) => ({ ...f, license_file_url: url || "", license_file_public_id: public_id || "" }))}
                    testid="drv-license-file"
                  />
                </Field>
              </div>
            </div>

            <ComplianceBlock title="Training" icon={GraduationCap} keyName="training" record={form.training} onChange={setSection} testidPrefix="drv-training" />
            <ComplianceBlock title="Medical Fitness" icon={Stethoscope} keyName="medical_fitness" record={form.medical_fitness} onChange={setSection} testidPrefix="drv-medical" />
            <ComplianceBlock title="Police Verification" icon={ShieldCheck} keyName="police_verification" record={form.police_verification} onChange={setSection} showExpiry={false} testidPrefix="drv-police" />

            <Field label="Remarks" full>
              <textarea data-testid="drv-input-remarks" rows={2} maxLength={1000} className={`${inputCls} resize-y`} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
            </Field>
          </div>

          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="drv-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update Driver" : "Add Driver")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete driver?</AlertDialogTitle>
            <AlertDialogDescription><b>{confirmDelete?.name}</b> and all uploaded documents will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="drv-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
